class CartUpsellRebuyComponent extends HTMLElement {
  constructor() {
    super();
    this.rebuyId = this.dataset.rebuyWidgetId;
    this.sectionName = 'cart-upsell-rebuy';
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
  }

  connectedCallback() {
    if (!this.rebuyId) return;
    this.getSection();
    this.subscribeToCartUpdate();
  }

  disconnectedCallback() {
    this.unsubscribeFromCartUpdate();
  }

  // ── Fallback visibility helpers ─────────────────────────────────────────
  // Priority chain: Rebuy > Native Addon > Recs. When Rebuy is configured, the
  // pre-rendered fallback (either the native addon OR the "You May Also Like"
  // recs carousel — only one is ever in the DOM) starts hidden. These methods
  // reveal/hide whichever fallback exists based on Rebuy's runtime state.

  _hideRecommendations() {
    document.querySelector('.cart-addon-native')?.setAttribute('hidden', '');
    document.querySelector('cart-recommendations')?.setAttribute('hidden', '');
  }

  _revealRecommendations() {
    document.querySelector('.cart-addon-native')?.removeAttribute('hidden');
    document.querySelector('cart-recommendations')?.removeAttribute('hidden');
  }

  // Polls for Rebuy's outcome after init() and toggles recommendations.
  // Runs in parallel with checkRebuyElement() so neither blocks the other.
  // Resolves when:
  //   • .rebuy-product-price found → Rebuy is showing → keep recs hidden
  //   • .rebuy-widget.is-hidden persists 600 ms without a price → reveal recs
  //   • 3 s timeout → conservatively reveal recs (cancels any pending confirm)
  // Selectors are scoped to the cart drawer upsell zone to avoid false
  // positives from other Rebuy widgets on the page.
  _syncRecommendationsVisibility() {
    // Clear any in-flight sync from a prior cart update
    clearInterval(this._recsSyncInterval);
    clearTimeout(this._recsSyncTimeout);
    clearTimeout(this._recsRevealConfirmTimeout);
    this._recsRevealConfirmTimeout = null;

    const zone = '.cart-drawer__rebuy-upsell';

    // The fallback may be an interactive add-on card, so revealing it and then
    // re-hiding it once Rebuy paints is jarring. A populated Rebuy widget can
    // transiently expose `.rebuy-widget.is-hidden` while loading, so we require
    // that "empty" signal to persist briefly before revealing the fallback —
    // if a price appears within the window, Rebuy wins and we never reveal.
    const REVEAL_CONFIRM_MS = 600;

    this._recsSyncInterval = setInterval(() => {
      if (document.querySelector(`${zone} .rebuy-product-price`)) {
        // Rebuy has products — cancel any pending reveal; fallback stays hidden.
        clearInterval(this._recsSyncInterval);
        clearTimeout(this._recsSyncTimeout);
        clearTimeout(this._recsRevealConfirmTimeout);
        this._recsRevealConfirmTimeout = null;
      } else if (document.querySelector(`${zone} .rebuy-widget.is-hidden`)) {
        // Rebuy looks empty — start the confirmation timer (once). The price
        // branch above cancels it if products show up during the window.
        if (!this._recsRevealConfirmTimeout) {
          this._recsRevealConfirmTimeout = setTimeout(() => {
            clearInterval(this._recsSyncInterval);
            clearTimeout(this._recsSyncTimeout);
            this._recsRevealConfirmTimeout = null;
            this._revealRecommendations();
          }, REVEAL_CONFIRM_MS);
        }
      } else if (this._recsRevealConfirmTimeout) {
        // `.is-hidden` vanished before confirming — Rebuy is still resolving.
        clearTimeout(this._recsRevealConfirmTimeout);
        this._recsRevealConfirmTimeout = null;
      }
    }, 150);

    this._recsSyncTimeout = setTimeout(() => {
      clearInterval(this._recsSyncInterval);
      clearTimeout(this._recsRevealConfirmTimeout);
      this._recsRevealConfirmTimeout = null;
      this._revealRecommendations();
    }, 3000);
  }

  // ── Section fetch / init ─────────────────────────────────────────────────

  getSection() {
    // Re-hide while Rebuy re-checks (handles repeated cart update cycles).
    this._hideRecommendations();

    const widget = window.Rebuy.widgets.find((widget) => {
      return widget.id === this.rebuyId;
    });

    if (widget && widget.detachWidget) {
      // There's a problem with the destroy method, it's not working as expected so we're using detachWidget instead
      widget?.detachWidget();
    }
    this.innerHTML = '';

    // Fetch section
    fetch(`${window.location.pathname}?sections=${this.sectionName}`)
      .then((response) => response.json())
      .then((data) => {
        const content = data[this.sectionName];
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;

        // Find the .shopify-section within the fetched content
        const shopifySection = tempDiv.querySelector('.shopify-section');

        if (shopifySection) {
          this.innerHTML = shopifySection.innerHTML;
          window.Rebuy.init();

          // Price badge setup — waits up to 10 s for .rebuy-product-price.
          // Also re-hides recommendations if they were prematurely revealed by
          // the 3 s timeout before Rebuy finished loading.
          this.checkRebuyElement().then((element) => {
            if (!element) return;

            this._hideRecommendations();
            this.setupPriceBadges();
            this.setupPriceObservers();

            this.checkReinit();
          });

          // Recommendations toggle — fast parallel check (3 s max)
          this._syncRecommendationsVisibility();
        } else {
          console.warn('No .shopify-section found in the fetched content.');
          // Rebuy section missing — fall through to recommendations
          this._revealRecommendations();
        }
      })
      .catch((error) => {
        console.warn('Error fetching and replacing section:', error);
        this._revealRecommendations();
      });
  }

  subscribeToCartUpdate() {
    if (window.PUB_SUB_EVENTS && window.subscribe) {
      this._unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
    } else {
      document.addEventListener('cart:update', this.handleCartUpdate);
    }
  }

  unsubscribeFromCartUpdate() {
    if (this._unsubscribe) {
      this._unsubscribe();
    } else {
      document.removeEventListener('cart:update', this.handleCartUpdate);
    }
  }

  handleCartUpdate(event) {
    const element = document.querySelector('.cart-drawer__rebuy-upsell');
    if (element) {
      element.removeAttribute('data-custom-initialized');
    }
    this.getSection();
  }

  async checkRebuyElement() {
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        const element = document.querySelectorAll('.rebuy-product-price');
        if (element.length) {
          clearInterval(interval);
          clearTimeout(timeout);
          resolve(element);
        }
      }, 100);

      const timeout = setTimeout(() => {
        clearInterval(interval);
        resolve(null);
      }, 10000);
    });
  }

  checkReinit() {
    const element = document.querySelector('.cart-drawer__rebuy-upsell');
    if (!element) return console.warn('No element found');
    if (element.dataset.isFetchingWidgetProducts) return;
    const rebuyId = element?.dataset?.rebuyId || this.rebuyId;
    const widget = window.Rebuy.widgets.find((widget) => widget.id === rebuyId);
    if (!widget) return console.warn('No widget found');
    element.dataset.isFetchingWidgetProducts = true;
    const priceWrappers = document.querySelectorAll('.rebuy-product-price');

    if (!priceWrappers.length && !!window?.Rebuy?.Cart?.cart?.items?.[0]) {
      widget.getWidgetProducts((products) => {
        if (products.length && !element.dataset.customInitialized) {
          window.Rebuy.init();
          element.dataset.customInitialized = true;
        }
        element.removeAttribute('data-is-fetching-widget-products');
      });

      // Use timeinterval to check rebuy-product-price and clear once found or after 5 seconds
      // This will insert badges and observers when the widget is loaded or when cart is refreshed
      let shouldClearInterval = true;
      const interval = setInterval(() => {
        const element = document.querySelectorAll('.rebuy-product-price');
        if (element.length) {
          shouldClearInterval = false;
          clearInterval(interval);
          // Initial setup of price badges
          this.setupPriceBadges();

          // Set up mutation observers to watch for price changes
          this.setupPriceObservers();
        }
      }, 100);

      setTimeout(() => {
        if (shouldClearInterval) {
          clearInterval(interval);
        }
      }, 5000);
    }
  }

  setupPriceBadges() {
    const priceWrappers = document.querySelectorAll('.rebuy-product-price');

    priceWrappers.forEach((priceWrapper) => {
      // Using the correct selector: .compare instead of .compare-at
      const compareElement = priceWrapper.querySelector('.compare-at');
      this.updatePriceBadge(priceWrapper, compareElement);
    });
  }

  setupPriceObservers() {
    const priceWrappers = document.querySelectorAll('.rebuy-product-price');

    priceWrappers.forEach((priceWrapper) => {
      // Using the correct selector
      const compareElement = priceWrapper.querySelector('.compare-at');

      if (compareElement) {
        // Set up observer for price changes
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'characterData' || mutation.type === 'childList') {
              // Update the badge whenever the price changes
              this.updatePriceBadge(priceWrapper, compareElement);
            }
          });
        });

        // Start observing with all necessary options
        observer.observe(compareElement, {
          characterData: true,
          childList: true,
          subtree: true,
        });
      }
    });
  }

  updatePriceBadge(priceWrapper, compareElement) {
    if (!compareElement) return;

    const saleElement = priceWrapper.querySelector('.sale').lastChild;

    // Only continue if both elements exist
    if (compareElement.lastChild && saleElement) {
      const currencySymbol = compareElement.lastChild.textContent.slice(0, 1);
      const compareAt = Number(compareElement.lastChild.textContent.slice(1).replace(/,/g, ''));
      const sale = Number(saleElement.textContent.slice(1).replace(/,/g, ''));

      // Only show the badge if compareAt is greater than sale
      if (compareAt > sale) {
        const savings = compareAt - sale;
        // Check if savings badge already exists
        const existingBadge = priceWrapper.querySelector('.rebuy-savings-badge');

        if (existingBadge) {
          // Update existing badge text
          existingBadge.textContent = window.productsStrings.save.replace(
            '[amount]',
            `${currencySymbol}${savings.toFixed(2)}`
          );
        } else {
          // Create new badge
          const savingsBadgeElement = document.createElement('span');
          savingsBadgeElement.classList.add('rebuy-savings-badge');
          savingsBadgeElement.textContent = window.productsStrings.save.replace(
            '[amount]',
            `${currencySymbol}${savings.toFixed(2)}`
          );
          priceWrapper.firstElementChild.appendChild(savingsBadgeElement);
        }
      }
    } else {
      // Remove existing badge if any
      const existingBadge = priceWrapper.querySelector('.rebuy-savings-badge');
      if (existingBadge) {
        existingBadge.remove();
      }
    }
  }
}

document.addEventListener('rebuy.loaded', (event) => {
  if (!customElements.get('cart-upsell-rebuy-component')) {
    customElements.define('cart-upsell-rebuy-component', CartUpsellRebuyComponent);
  }
});

document.addEventListener('rebuy.add', function (event) {
  // ref: cart-remove-button component in cart.js
  const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
  if (cartItems) {
    cartItems.onCartUpdate();
  }
});
